import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '../constants/featureFlags';
import { ReferralCode, ReferralCodeHouseAccount } from '../types';
import ConfirmationModal from '../modals/ConfirmationModal';
import { ReferralCodeStatusBadge, formatReferralDate } from './referralUi';

const INPUT = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';
const LABEL = 'block text-sm font-medium text-gray-700 mb-1';
const TH = 'px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
const TD = 'px-2 md:px-6 py-4 text-sm text-gray-900';
const ROW_ACTION = 'text-blue-600 hover:text-blue-800 text-sm underline whitespace-nowrap disabled:opacity-50';
const CODE_PATTERN = /^[A-Z0-9]{1,16}$/;

const normaliseCode = (value: string) => value.replace(/\s+/g, '').toUpperCase();

const ReferralCodesList: React.FC = () => {
  const [codes, setCodes] = useState<ReferralCode[]>([]);
  const canManage = useFeatureFlag(FEATURE_FLAGS.REFERRAL_PROGRAMME);
  const [houseAccount, setHouseAccount] = useState<ReferralCodeHouseAccount | null>(null);
  const [houseInviteCode, setHouseInviteCode] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [formErrors, setFormErrors] = useState<{ name?: string; code?: string }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [toDeactivate, setToDeactivate] = useState<ReferralCode | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const res = await fetchWithAuth('/referral_codes');
      if (!res.ok) throw new Error('Failed to fetch referral codes');
      const data = await res.json();
      setCodes(data.referral_codes);
      setHouseAccount(data.house_account);
      setHouseInviteCode(data.house_invite_code);
    } catch (error) {
      console.error('Error fetching referral codes:', error);
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalised = normaliseCode(code);
    const errors = {
      name: name.trim() ? undefined : 'A name is required',
      code: CODE_PATTERN.test(normalised) ? undefined : 'Codes are 1 to 16 letters or digits, with no spaces or symbols',
    };
    setFormErrors(errors);
    if (errors.name || errors.code) return;

    setIsSaving(true);
    try {
      const res = await fetchWithAuth('/referral_codes', {
        method: 'POST',
        body: JSON.stringify({ referral_code: { name: name.trim(), code: normalised } }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.errors?.[0] || 'Failed to create the code');
        return;
      }
      setCodes(current => [data.referral_code, ...current]);
      setName('');
      setCode('');
      toast.success(`${data.referral_code.code} created`);
    } catch {
      toast.error('Failed to create the code');
    } finally {
      setIsSaving(false);
    }
  };

  const setActive = async (referralCode: ReferralCode, active: boolean) => {
    setUpdatingId(referralCode.id);
    try {
      const res = await fetchWithAuth(`/referral_codes/${referralCode.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ referral_code: { active } }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.errors?.[0] || 'Failed to update the code');
        return;
      }
      setCodes(current => current.map(row => (row.id === referralCode.id ? data.referral_code : row)));
      toast.success(`${referralCode.code} ${active ? 'reactivated' : 'deactivated'}`);
    } catch {
      toast.error('Failed to update the code');
    } finally {
      setUpdatingId(null);
    }
  };

  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Invite link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };

  return (
    <>
      {loadFailed && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-800 rounded" role="alert">
          Couldn't load the custom referral codes. Refresh the page to try again.
        </div>
      )}

      {canManage && !isLoading && !loadFailed && !houseAccount && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6" role="alert">
          <p className="text-sm text-amber-800">
            There's no Tini Express account with the invite code {houseInviteCode}, so custom referral codes can't be created yet.
          </p>
        </div>
      )}

      {canManage && (
        <form onSubmit={createCode} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6" noValidate>
          <h2 className="text-lg font-semibold text-gray-900 mb-1">New Custom Referral Code</h2>
          <p className="text-sm text-gray-600 mb-4">
            Friends who sign up with a custom referral code are credited to{' '}
            {houseAccount ? (
              <Link to={`/customers/${houseAccount.id}`} className="text-blue-600 hover:underline">
                {houseAccount.name} ({houseAccount.code})
              </Link>
            ) : (
              'the Tini Express account'
            )}
            , and get the same welcome discount as any other referral.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="referralCodeName" className={LABEL}>
                Name
              </label>
              <input
                id="referralCodeName"
                type="text"
                value={name}
                maxLength={100}
                onChange={e => setName(e.target.value)}
                placeholder="Manzini radio ad, Nov 2026"
                className={`${INPUT} ${formErrors.name ? 'border-red-400' : ''}`}
              />
              {formErrors.name && <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>}
            </div>
            <div>
              <label htmlFor="referralCodeCode" className={LABEL}>
                Code
              </label>
              <input
                id="referralCodeCode"
                type="text"
                value={code}
                maxLength={16}
                onChange={e => setCode(normaliseCode(e.target.value))}
                placeholder="MANZINIFM"
                className={`${INPUT} font-mono uppercase ${formErrors.code ? 'border-red-400' : ''}`}
                aria-describedby="referralCodeHint"
              />
              {formErrors.code && <p className="text-red-500 text-sm mt-1">{formErrors.code}</p>}
              <p id="referralCodeHint" className="text-xs text-gray-500 mt-1">
                Up to 16 letters or digits. It can't be changed later.
              </p>
            </div>
            <div className="flex items-start md:pt-6">
              <button
                type="submit"
                disabled={isSaving || !houseAccount}
                className="w-full bg-black text-white px-4 py-2 rounded hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Creating...' : 'Create Code'}
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className={TH}>Code</th>
                <th className={TH}>Status</th>
                <th className={`${TH} whitespace-nowrap`}>Sign-ups</th>
                <th className={`hidden md:table-cell ${TH}`}>First Paid Orders</th>
                <th className={`hidden lg:table-cell ${TH}`}>Created</th>
                {canManage && (
                  <th className={TH}>
                    <span className="sr-only">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={canManage ? 6 : 5} className="px-6 py-12 text-center text-gray-500">
                    Loading custom referral codes...
                  </td>
                </tr>
              ) : (
                <>
                  {houseAccount && (
                    <tr className="hover:bg-gray-50" data-testid="referral-code-house">
                      <td className={TD}>
                        <div className="font-mono font-medium whitespace-nowrap">{houseAccount.invite_code}</div>
                        <div className="text-gray-500 break-words">{houseAccount.name} account's own code</div>
                      </td>
                      <td className={TD}>
                        <ReferralCodeStatusBadge active />
                      </td>
                      <td className={TD}>{houseAccount.sign_ups}</td>
                      <td className={`hidden md:table-cell ${TD}`}>{houseAccount.first_orders}</td>
                      <td className={`hidden lg:table-cell ${TD} text-gray-500`}>N/A</td>
                      {canManage && (
                        <td className={TD}>
                          <div className="flex flex-wrap justify-end gap-x-4 gap-y-1">
                            {houseAccount.invite_link && (
                              <button
                                type="button"
                                onClick={() => copyLink(houseAccount.invite_link as string)}
                                className={ROW_ACTION}
                                aria-label={`Copy invite link for ${houseAccount.invite_code}`}
                              >
                                Copy Link
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  )}
                  {loadFailed ? null : codes.length === 0 ? (
                    <tr>
                      <td colSpan={canManage ? 6 : 5} className="px-6 py-12 text-center text-gray-500">
                        No custom referral codes yet
                      </td>
                    </tr>
                  ) : (
                    codes.map(row => (
                      <tr key={row.id} className="hover:bg-gray-50" data-testid={`referral-code-${row.id}`}>
                        <td className={TD}>
                          <div className="font-mono font-medium whitespace-nowrap">{row.code}</div>
                          <div className="text-gray-500 break-words">{row.name}</div>
                        </td>
                        <td className={TD}>
                          <ReferralCodeStatusBadge active={row.active} />
                        </td>
                        <td className={TD}>{row.sign_ups}</td>
                        <td className={`hidden md:table-cell ${TD}`}>{row.first_orders}</td>
                        <td className={`hidden lg:table-cell ${TD}`}>
                          <div className="whitespace-nowrap">{formatReferralDate(row.created_at)}</div>
                          <div className="text-gray-500 break-all">{row.created_by_email || 'N/A'}</div>
                        </td>
                        {canManage && (
                          <td className={TD}>
                            <div className="flex flex-wrap justify-end gap-x-4 gap-y-1">
                              {row.invite_link && (
                                <button
                                  type="button"
                                  onClick={() => copyLink(row.invite_link as string)}
                                  className={ROW_ACTION}
                                  aria-label={`Copy invite link for ${row.code}`}
                                >
                                  Copy Link
                                </button>
                              )}
                              {row.active ? (
                                <button
                                  type="button"
                                  onClick={() => setToDeactivate(row)}
                                  disabled={updatingId === row.id}
                                  className="text-red-500 hover:text-red-700 text-sm whitespace-nowrap disabled:opacity-50"
                                  aria-label={updatingId === row.id ? `Saving ${row.code}` : `Deactivate ${row.code}`}
                                >
                                  {updatingId === row.id ? 'Saving...' : 'Deactivate'}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setActive(row, true)}
                                  disabled={updatingId === row.id}
                                  className={ROW_ACTION}
                                  aria-label={updatingId === row.id ? `Saving ${row.code}` : `Reactivate ${row.code}`}
                                >
                                  {updatingId === row.id ? 'Saving...' : 'Reactivate'}
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmationModal
        isOpen={toDeactivate !== null}
        message={
          toDeactivate &&
          `Deactivate ${toDeactivate.code}? New sign-ups using it will be told the code isn't valid. Friends who already signed up keep their discount.`
        }
        onCancel={() => setToDeactivate(null)}
        onConfirm={() => {
          if (toDeactivate) setActive(toDeactivate, false);
          setToDeactivate(null);
        }}
      />
    </>
  );
};

export default ReferralCodesList;
